import React, { useEffect, useState } from 'react';
import { TrueFalseActivity as TrueFalseActivityType } from '../../types';
import Button from '../common/Button';
import { ArrowRight, Check, Mic, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { updateUserScores } from '../../services/leaderboardService';
import { useAuth } from '../../contexts/AuthContext';
import { incrementChallengeScore } from '../../services/challengeService';
import { logUserActivity } from '../../services/activityLogService';

interface TrueFalseActivityProps {
  onComplete: () => void;
  setPoints: (value: number) => void;
  onPointsEarned: (points: number) => void;
  getTrueOrFalseActivity: (activity: string, word: object) => Promise<boolean>;
  answer: boolean;
  question: string;
  currentWord: object | undefined | null;
  saveDayExercise: (activity: string) => void;
  getDayExercise: (activity: string, word: object) => Promise<boolean>;
  setShowReward: (value: boolean) => void;
  allActivitiesCompleted: boolean;
  setTrueFalseActivity: (value: boolean) => void;
  setSpeakyActivity: (value: boolean) => void;
  noExamples: boolean;
  wordLoading: boolean;
}

const TrueFalseActivity: React.FC<TrueFalseActivityProps> = ({ setPoints, onPointsEarned, getTrueOrFalseActivity, onComplete, answer, question, currentWord, saveDayExercise, getDayExercise, setShowReward, allActivitiesCompleted, setTrueFalseActivity, setSpeakyActivity, noExamples }) => {
  const [userAnswer, setUserAnswer] = useState<boolean | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleAnswerSelect = (answer: boolean) => {
    if (hasSubmitted) return;
    setUserAnswer(answer);
  };

  const checkExercise = async () => {
    const isCompleted = await getDayExercise('trueOrFalse', currentWord);
    if (isCompleted) {
      setHasSubmitted(true);
      setIsCorrect(true);
    } else {
      setHasSubmitted(false);
      setIsCorrect(false);
    }
  }


  const handleSubmit = () => {
    if (userAnswer === null || hasSubmitted) return;
    
    const correct = userAnswer === answer;
    setIsCorrect(correct);
    setHasSubmitted(true);
    saveDayExercise('trueOrFalse');
    
    if (correct) {
      let earned = 25;
      if (failedAttempts === 0) earned = 100;
      else if (failedAttempts === 1) earned = 50;
      onPointsEarned(earned);
      setPoints(earned);

      setShowReward(true);
      setTimeout(() => {
        setShowReward(false);
        onComplete();
      }, 1500);
    } else {
      setFailedAttempts(prev => prev + 1);
    }
  };

  const handleReset = () => {
    setUserAnswer(null);
    setHasSubmitted(false);
    setIsCorrect(false);
    getTrueOrFalseActivity();
  };

  useEffect(() => {
    checkExercise();
  }, []);


  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">True or False</h3>
      
      <div className="mb-6">
        <p className="text-lg text-gray-700">{`${currentWord?.word?.charAt(0).toUpperCase() + currentWord.word.slice(1)} means ${question.toLowerCase().replace('.', '')}?`}</p>
      </div>
      
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => handleAnswerSelect(true)}
          className={`flex-1 py-3 px-4 rounded-lg transition-colors ${
            userAnswer === true
              ? hasSubmitted
                ? answer
                  ? 'bg-green-100 border-green-300 border'
                  : 'bg-red-100 border-red-300 border'
                : 'bg-blue-100 border-blue-300 border'
              : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
          }`}
          disabled={hasSubmitted}
        >
          <div className="flex justify-center items-center">
            <span className="font-medium">True</span>
            {hasSubmitted && answer && (
              <Check className="ml-2 text-green-600 h-5 w-5" />
            )}
          </div>
        </button>
        
        <button
          onClick={() => handleAnswerSelect(false)}
          className={`flex-1 py-3 px-4 rounded-lg transition-colors ${
            userAnswer === false
              ? hasSubmitted
                ? !answer
                  ? 'bg-green-100 border-green-300 border'
                  : 'bg-red-100 border-red-300 border'
                : 'bg-blue-100 border-blue-300 border'
              : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
          }`}
          disabled={hasSubmitted}
        >
          <div className="flex justify-center items-center">
            <span className="font-medium">False</span>
            {hasSubmitted && !answer && (
              <Check className="ml-2 text-green-600 h-5 w-5" />
            )}
          </div>
        </button>
      </div>
      
      {hasSubmitted && (
        <div className={`p-4 rounded-lg mb-4 ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <div className="flex items-center">
            {isCorrect ? (
              <Check className="h-5 w-5 mr-2" />
            ) : (
              <X className="h-5 w-5 mr-2" />
            )}
            <p className="font-medium">
              {isCorrect 
                ? 'Correct! Well done!' 
                : `Incorrect. The statement is ${answer ? 'true' : 'false'}.`}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        {hasSubmitted && !isCorrect ? (
          <Button variant="outline" onClick={handleReset}>
            Try Again
          </Button>
        ) : hasSubmitted && isCorrect && !noExamples ? (
          <Button onClick={() => onComplete()}>
            Next Activity
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={hasSubmitted}
          >
            {hasSubmitted  ? 'Completed' : 'Check Answer'}
          </Button>
        )}
        {hasSubmitted && !isCorrect && !allActivitiesCompleted && !noExamples && (
          <Button variant="outline" onClick={() => {
            onPointsEarned(0);
            if(!noExamples) {
              setTrueFalseActivity(false);
              setSpeakyActivity(true);
            }
          }}>
            Skip
          </Button>
        )}
      </div>
      {noExamples && (
              <div className="mt-5 bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
                <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                  {/* Icon + Text Section */}
                  <div className="flex flex-col items-center md:flex-row md:items-center">
                    <div className="bg-purple-100 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900">All activities completed!</h3>
                      <p className="text-purple-700 mt-2">Great job! Try a new word to continue learning.</p>
                    </div>
                  </div>
              
                  {/* Button Section */}
                  <div className="mt-4 md:mt-0 md:ml-auto">
                    <Button
                      onClick={() => navigate('/themes')}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-400 transition duration-200"
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      New Word
                    </Button>
                  </div>
                </div>
              </div>    
            )}
    </div>
  );
};

export default TrueFalseActivity;